import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import * as authApi from '@/api/auth';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { checkPasswordPolicy, PASSWORD_RULES } from '@/utils/passwordPolicy';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    const policyError = checkPasswordPolicy(password);
    if (policyError) {
      setError(policyError);
      return;
    }
    setLoading(true);
    try {
      await authApi.acceptInvite({ token, email, password, name: name || undefined });
      navigate('/');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Invite link is invalid or has expired.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-sm text-gray-600">
          Invalid invite link.{' '}
          <Link to="/login" className="text-primary-600 hover:underline">
            Go to sign in.
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="Ashcourt" className="mx-auto mb-5 h-12 w-auto" />
          <h1 className="text-2xl font-bold text-white">Accept invite</h1>
          <p className="mt-1 text-sm text-slate-400">
            You've been invited to join Ashcourt Cashflow.
          </p>
        </div>

        <div className="rounded-xl border border-slate-700 bg-navy-800 p-8 shadow-lg [&_.input-field]:bg-navy-900 [&_.input-field]:border-slate-600 [&_.input-field]:text-white [&_.input-field]:placeholder-slate-400 [&_label]:text-slate-200">
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {error && (
              <div role="alert" className="rounded-lg border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="rounded-lg bg-navy-900/50 px-4 py-3">
              <p className="text-xs text-slate-400">Joining as</p>
              <p className="text-sm font-medium text-slate-100">{email}</p>
            </div>

            <Input
              label="Your name (optional)"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              placeholder="e.g. Jane Smith"
            />

            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            <ul className="-mt-2 space-y-1 text-xs text-slate-400">
              {PASSWORD_RULES.map((rule) => (
                <li key={rule} className="flex items-center gap-2">
                  <span aria-hidden="true" className="text-slate-500">•</span>
                  {rule}
                </li>
              ))}
            </ul>

            <Input
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={loading}
            />

            <Button type="submit" loading={loading} className="w-full">
              Set password and sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
